import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";

import ImageUpload from "../../../common/ImageUpload";
import { Section, renderSectionTitle } from "../../../common/FormUtils";
import { Product } from "../../../../types/Shop";
import {
  useAuthorProducts,
  useAuthorShop,
  upsertAuthorShop,
  createAuthorProduct,
  updateAuthorProduct,
  deleteAuthorProduct,
} from "../../../../database/shop/authorShop";

type Props = {
  authorId?: string;
  mode?: string;
};

const inputStyle: React.CSSProperties = { width: "100%", padding: 8 };
const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "#666",
  marginBottom: 4,
};

/**
 * In-dashboard editor for an author's shop (Author/{id}/Shop). Self-contained:
 * products save individually and shop settings save on their own button, so it
 * can live inside the author Formik form without nesting form submissions.
 */
const AuthorShopSection: React.FC<Props> = ({ authorId, mode }) => {
  const isDetail = mode === "detail";
  const products = useAuthorProducts({ authorId });
  const shop = useAuthorShop({ authorId });

  const [editingProducts, setEditingProducts] = useState<{
    [key: string]: Product;
  }>({});
  const [settings, setSettings] = useState({
    shopName: "",
    currency: "USD",
    paymentLink: "",
  });
  const [settingsSaved, setSettingsSaved] = useState(false);

  useEffect(() => {
    if (!shop) return;
    setSettings({
      shopName: shop.shopName || "",
      currency: shop.currency || "USD",
      paymentLink: shop.paymentLink || "",
    });
  }, [shop]);

  if (!authorId) {
    return (
      <Section>
        {renderSectionTitle("Shop")}
        <p style={{ color: "#888", fontSize: 13 }}>
          Save the author first, then reopen to manage their shop and offerings.
        </p>
      </Section>
    );
  }

  const saveSettings = async () => {
    await upsertAuthorShop({ authorId, shop: settings });
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2000);
  };

  const addProduct = () => {
    const newProduct: Product = {
      id: `product-${Date.now()}`,
      name: "",
      description: "",
      price: 0,
      imageURL: "",
      category: "",
      index: products.length,
      isVisible: true,
      itemLink: "",
    };
    setEditingProducts((prev) => ({ ...prev, [newProduct.id]: newProduct }));
  };

  const updateEditingProduct = (
    productId: string,
    field: keyof Product,
    value: any
  ) => {
    setEditingProducts((prev) => {
      const product = prev[productId];
      if (!product) return prev;
      return { ...prev, [productId]: { ...product, [field]: value } };
    });
  };

  const startEditingProduct = (product: Product) => {
    setEditingProducts((prev) => ({ ...prev, [product.id]: { ...product } }));
  };

  const cancelEditingProduct = (productId: string) => {
    setEditingProducts((prev) => {
      const { [productId]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const saveProduct = async (product: Product) => {
    try {
      if (products.find((p) => p.id === product.id)) {
        await updateAuthorProduct({ authorId, product });
      } else {
        await createAuthorProduct({ authorId, product });
      }
      cancelEditingProduct(product.id);
    } catch (error) {
      console.error("Error saving author product", error);
    }
  };

  const removeProduct = async (productId: string) => {
    try {
      await deleteAuthorProduct({ authorId, productId });
      cancelEditingProduct(productId);
    } catch (error) {
      console.error("Error removing author product", error);
    }
  };

  const productImageField = (product: Product) => ({
    name: `author-product-${product.id}-imageURL`,
    value: product.imageURL || "",
  });
  const productImageForm = (productId: string) => ({
    setFieldValue: (_field: string, value: string) =>
      updateEditingProduct(productId, "imageURL", value),
  });

  const displayProducts = products.filter((p) => !editingProducts[p.id]);

  return (
    <Section>
      {renderSectionTitle("Shop")}
      <p style={{ color: "#888", fontSize: 12, marginBottom: 16 }}>
        The author's own storefront. Products save individually; shop settings
        save on their own button. Enabling any product turns the shop on in the
        app.
      </p>

      {/* Shop settings */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div>
          <label style={labelStyle}>Shop name</label>
          <input
            type="text"
            placeholder="Offerings"
            value={settings.shopName}
            disabled={isDetail}
            onChange={(e) =>
              setSettings((s) => ({ ...s, shopName: e.target.value }))
            }
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Currency</label>
          <input
            type="text"
            placeholder="USD"
            value={settings.currency}
            disabled={isDetail}
            onChange={(e) =>
              setSettings((s) => ({ ...s, currency: e.target.value }))
            }
            style={inputStyle}
          />
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <label style={labelStyle}>Payment link (optional)</label>
        <input
          type="text"
          placeholder="Checkout / payment gateway URL"
          value={settings.paymentLink}
          disabled={isDetail}
          onChange={(e) =>
            setSettings((s) => ({ ...s, paymentLink: e.target.value }))
          }
          style={inputStyle}
        />
      </div>
      {!isDetail && (
        <div style={{ marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            onClick={saveSettings}
          >
            Save shop settings
          </Button>
          {settingsSaved && (
            <span style={{ color: "#2e7d32", fontSize: 13 }}>Saved</span>
          )}
        </div>
      )}

      {/* Products */}
      {renderSectionTitle("Products")}
      <div style={{ marginBottom: 16 }}>
        {displayProducts.map((product) => (
          <div
            key={product.id}
            style={{
              marginBottom: 16,
              padding: 16,
              border: "1px solid #ddd",
              borderRadius: 4,
              backgroundColor: "#fff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 style={{ margin: 0 }}>{product.name || "Unnamed Product"}</h4>
              {!isDetail && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={() => startEditingProduct(product)}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    color="secondary"
                    size="small"
                    onClick={() => removeProduct(product.id)}
                  >
                    Delete
                  </Button>
                </div>
              )}
            </div>
            <p style={{ margin: "8px 0 0 0", fontSize: 12, color: "#666" }}>
              Price: {settings.currency || "$"}
              {product.price} | Category: {product.category || "N/A"} | Index:{" "}
              {product.index}
            </p>
          </div>
        ))}

        {Object.values(editingProducts).map((product) => (
          <div
            key={product.id}
            style={{
              marginBottom: 16,
              padding: 16,
              border: "1px solid #ddd",
              borderRadius: 4,
              backgroundColor: "#f9f9f9",
            }}
          >
            <h4 style={{ marginTop: 0, marginBottom: 12 }}>
              {product.name || "New Product"}
            </h4>

            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Name</label>
              <input
                type="text"
                value={product.name || ""}
                disabled={isDetail}
                onChange={(e) =>
                  updateEditingProduct(product.id, "name", e.target.value)
                }
                style={inputStyle}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Description</label>
              <textarea
                value={product.description || ""}
                disabled={isDetail}
                onChange={(e) =>
                  updateEditingProduct(product.id, "description", e.target.value)
                }
                style={{ ...inputStyle, minHeight: 60 }}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <div>
                <label style={labelStyle}>Price</label>
                <input
                  type="number"
                  value={product.price || 0}
                  disabled={isDetail}
                  onChange={(e) =>
                    updateEditingProduct(
                      product.id,
                      "price",
                      parseFloat(e.target.value) || 0
                    )
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Category</label>
                <input
                  type="text"
                  value={product.category || ""}
                  disabled={isDetail}
                  onChange={(e) =>
                    updateEditingProduct(product.id, "category", e.target.value)
                  }
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Product Image</label>
              <ImageUpload
                field={productImageField(product)}
                form={productImageForm(product.id)}
                actionId={product.id}
                reference={"author-shop/products"}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={labelStyle}>Item Link</label>
              <input
                type="text"
                placeholder="External link / checkout for this offering"
                value={product.itemLink || ""}
                disabled={isDetail}
                onChange={(e) =>
                  updateEditingProduct(product.id, "itemLink", e.target.value)
                }
                style={inputStyle}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <div>
                <label style={labelStyle}>Index</label>
                <input
                  type="number"
                  value={product.index ?? 0}
                  disabled={isDetail}
                  onChange={(e) =>
                    updateEditingProduct(
                      product.id,
                      "index",
                      parseInt(e.target.value) || 0
                    )
                  }
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Visible</label>
                <select
                  value={product.isVisible ? "true" : "false"}
                  disabled={isDetail}
                  onChange={(e) =>
                    updateEditingProduct(
                      product.id,
                      "isVisible",
                      e.target.value === "true"
                    )
                  }
                  style={inputStyle}
                >
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>
            </div>

            {!isDetail && (
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={() => saveProduct(product)}
                >
                  Save Product
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  color="secondary"
                  onClick={() => removeProduct(product.id)}
                >
                  Delete Product
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => cancelEditingProduct(product.id)}
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        ))}

        {!isDetail && (
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={addProduct}
          >
            Add Product
          </Button>
        )}
      </div>
    </Section>
  );
};

export default AuthorShopSection;
